import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Save, Building2 } from "lucide-react";
import { toast } from "sonner";

const F = ({ label, className = "", children }) => (
  <label className={`text-sm ${className}`}>
    <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

const EMPTY = {
  business_name: "", legal_name: "", logo_url: "",
  address_line1: "", address_line2: "", city: "", district: "", state: "", pincode: "", country: "India",
  phone: "", email: "", website: "",
  gstin: "", pan: "", fssai: "",
  bank_name: "", bank_account_name: "", bank_account_no: "", bank_ifsc: "", bank_branch: "", bank_upi: "",
  invoice_prefix: "INV", invoice_terms: "", invoice_footer_note: "", invoice_signature_url: "",
  customer_code_prefix: "CUST", customer_code_pad: 5,
  price_level_1_name: "MRP", price_level_2_name: "Retail", price_level_3_name: "Wholesale", price_level_4_name: "Special",
  grn_qty_tolerance_pct: 10,
};

export default function BusinessSettings() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/sales/business-settings")
      .then((r) => setForm({ ...EMPTY, ...(r.data || {}) }))
      .finally(() => setLoading(false));
  }, []);

  const lookupPin = async () => {
    if (!/^\d{6}$/.test(form.pincode || "")) return;
    try {
      const r = await api.get(`/sales/pincode/${form.pincode}`);
      setForm((f) => ({
        ...f,
        district: f.district || r.data.district || "",
        state: f.state || r.data.state || "",
        city: f.city || (r.data.localities?.[0]?.name || ""),
      }));
      toast.success("Pincode looked up");
    } catch (e) { toast.error(e?.response?.data?.detail || "Pincode lookup failed"); }
  };

  const save = async () => {
    if (!form.business_name.trim()) return toast.error("Business name is required");
    setSaving(true);
    try {
      await api.put("/sales/business-settings", form);
      toast.success("Business settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageSection
        title="Business Settings"
        description="HQ profile, statutory info, banking and invoice defaults. Applied to every invoice generated from HQ."
        action={
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving} data-testid="biz-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Settings"}
          </Button>
        }
      >
        <Section title="Business" icon={Building2}>
          <F label="Business Name *"><Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="biz-name" /></F>
          <F label="Legal Name"><Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="biz-legal-name" /></F>
          <F label="Logo URL" className="col-span-2"><Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." data-testid="biz-logo" /></F>
        </Section>

        <Section title="Contact & Address">
          <F label="Address Line 1" className="col-span-2"><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} data-testid="biz-addr1" /></F>
          <F label="Address Line 2" className="col-span-2"><Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid="biz-addr2" /></F>
          <F label="Pincode">
            <div className="flex gap-2">
              <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))} onBlur={lookupPin} className="font-mono" data-testid="biz-pincode" />
              <Button variant="outline" size="sm" onClick={lookupPin} disabled={form.pincode?.length !== 6} data-testid="biz-pincode-lookup">Fetch</Button>
            </div>
          </F>
          <F label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="biz-city" /></F>
          <F label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} data-testid="biz-district" /></F>
          <F label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="biz-state" /></F>
          <F label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="biz-phone" /></F>
          <F label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="biz-email" /></F>
          <F label="Website" className="col-span-2"><Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="biz-website" /></F>
        </Section>

        <Section title="Statutory">
          <F label="GSTIN"><Input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="font-mono" data-testid="biz-gstin" /></F>
          <F label="PAN"><Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="font-mono" data-testid="biz-pan" /></F>
          <F label="FSSAI (optional)"><Input value={form.fssai} onChange={(e) => set("fssai", e.target.value)} className="font-mono" data-testid="biz-fssai" /></F>
        </Section>

        <Section title="Bank">
          <F label="Bank Name"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="biz-bank-name" /></F>
          <F label="Account Holder Name"><Input value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} data-testid="biz-bank-holder" /></F>
          <F label="Account Number"><Input value={form.bank_account_no} onChange={(e) => set("bank_account_no", e.target.value)} className="font-mono" data-testid="biz-bank-acno" /></F>
          <F label="IFSC"><Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} className="font-mono" data-testid="biz-bank-ifsc" /></F>
          <F label="Branch"><Input value={form.bank_branch} onChange={(e) => set("bank_branch", e.target.value)} data-testid="biz-bank-branch" /></F>
          <F label="UPI ID"><Input value={form.bank_upi} onChange={(e) => set("bank_upi", e.target.value)} data-testid="biz-bank-upi" /></F>
        </Section>

        <Section title="Invoice Defaults">
          <F label="Invoice # Prefix">
            <Input value={form.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value.toUpperCase())} className="font-mono" data-testid="biz-inv-prefix" />
            <span className="text-[10px] text-gray-500 mt-1 block">Example: <span className="font-mono">{form.invoice_prefix || "INV"}-2026-00001</span></span>
          </F>
          <F label="Signature URL"><Input value={form.invoice_signature_url} onChange={(e) => set("invoice_signature_url", e.target.value)} placeholder="https://..." data-testid="biz-inv-sig" /></F>
          <F label="Terms & Conditions" className="col-span-2">
            <textarea value={form.invoice_terms} onChange={(e) => set("invoice_terms", e.target.value)} rows={4} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y" placeholder="e.g. Goods once sold will not be taken back..." data-testid="biz-inv-terms" />
          </F>
          <F label="Footer Note" className="col-span-2">
            <Input value={form.invoice_footer_note} onChange={(e) => set("invoice_footer_note", e.target.value)} placeholder="Thank you for your business" data-testid="biz-inv-footer" />
          </F>
        </Section>

        <Section title="Customer Code Auto-Generation">
          <F label="Customer Code Prefix">
            <Input value={form.customer_code_prefix} onChange={(e) => set("customer_code_prefix", e.target.value.toUpperCase())} className="font-mono" data-testid="biz-cus-prefix" />
            <span className="text-[10px] text-gray-500 mt-1 block">Example next code: <span className="font-mono">{(form.customer_code_prefix || "CUST")}-{"1".padStart(Number(form.customer_code_pad) || 5, "0")}</span></span>
          </F>
          <F label="Zero-Padding Width">
            <Input type="number" min={3} max={8} value={form.customer_code_pad} onChange={(e) => set("customer_code_pad", Math.max(3, Math.min(8, Number(e.target.value) || 5)))} className="font-mono" data-testid="biz-cus-pad" />
          </F>
        </Section>

        <Section title="Pricing Levels (rename to your shop's labels)">
          <F label="Level 1"><Input value={form.price_level_1_name} onChange={(e) => set("price_level_1_name", e.target.value)} data-testid="biz-pl1" /></F>
          <F label="Level 2"><Input value={form.price_level_2_name} onChange={(e) => set("price_level_2_name", e.target.value)} data-testid="biz-pl2" /></F>
          <F label="Level 3"><Input value={form.price_level_3_name} onChange={(e) => set("price_level_3_name", e.target.value)} data-testid="biz-pl3" /></F>
          <F label="Level 4"><Input value={form.price_level_4_name} onChange={(e) => set("price_level_4_name", e.target.value)} data-testid="biz-pl4" /></F>
        </Section>

        <Section title="GRN Entry Tolerance">
          <F label="Qty Tolerance % from PO">
            <Input type="number" step="0.5" min={0} max={100} value={form.grn_qty_tolerance_pct}
              onChange={(e) => set("grn_qty_tolerance_pct", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              className="tabular-nums" data-testid="biz-grn-tolerance" />
            <span className="text-[10px] text-gray-500 mt-1 block">
              When a GRN is loaded from a PO, each line&apos;s received qty must stay within this % band of the ordered qty.
              Example at 10%: PO qty 100 allows GRN qty 90–110. Set to 0 to require exact match. Standalone GRNs (no PO) are never limited.
            </span>
          </F>
        </Section>
      </PageSection>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Surface className="mb-4">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        {Icon && <Icon className="h-4 w-4 text-gray-500" />}
        <h3 className="font-heading font-semibold text-sm text-gray-900">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </Surface>
  );
}
